import { Link } from 'react-router-dom';
import type { Extra, Venue } from '../lib/content';
import { IconLeaf } from './Icon';
import { dayKey, fmtTime } from '../lib/time';

/**
 * The day's massage, as one line on Now.
 *
 * **A notice, not a card.** The ask was small, non-intrusive and readily
 * visible, and those pull against each other: another bordered card in the rail
 * would be visible and anything but non-intrusive, and a line in the footer
 * would be neither. So it is a single tinted row at the foot of the rail —
 * shorter than everything above it, tinted rather than bordered so it reads as
 * an aside rather than as a fourth thing to deal with, and still a tap through
 * to the map because "where is the Hospitality Lounge" is the next question.
 *
 * **It appears on three days of the year and is absent on the other 362.** That
 * is what keeps it non-intrusive: it is not a standing row that says "no
 * massage today" on a Tuesday in March. Same test the playlist gets — it is
 * gone once the window closes, because "14:00–16:00" at half past four is a
 * dead row telling a guest to go somewhere nobody is.
 *
 * The times are printed from the timestamps rather than written into the
 * content, so they come out 24-hour in Khao Lak like every other time on the
 * site. The source said "2-4pm"; the site has never said pm anywhere.
 */

/**
 * Today's, and only while there is still some of it left.
 *
 * Exported for its test, and pure. `dayKey` rather than arithmetic on the
 * timestamp, so "today" means the date *in Khao Lak* — a guest opening this at
 * 23:00 in London is asking about a day that started six hours ago there.
 *
 * Returns every match rather than the first. There is one a day today, so this
 * renders one row; if a second is ever added to the same day, the alternative
 * to showing both is dropping one silently, which is not a thing content should
 * be able to do to itself.
 *
 * **"Today" is keyed off the start, so a window crossing midnight would end at
 * midnight.** None of the three do, and an extra is a daytime offer by nature —
 * but `events` has two that run to 3am, so it is a shape this file has seen
 * next door. If one is ever added here, this is the line to change: the fix is
 * to keep it while `at` is inside the window regardless of the day, and the
 * reason it is not written that way now is that it would be a branch no data
 * reaches and no test could honestly justify.
 */
export function extrasToday(extras: Extra[], at: number): Extra[] {
  return extras
    // **The window is only tested when there is one.** `end ?? start` reads as
    // the obvious default and is exactly backwards here: it expires the entry
    // the moment it opens, so an offer with no closing time would appear all
    // morning and vanish at the instant it became available. An `Extra` with no
    // `end` is open-ended — the row says "from 14:00" — so it stands until the
    // day does, which is what the `dayKey` test already decides.
    .filter((e) => dayKey(e.start) === dayKey(at) && (e.end ? Date.parse(e.end) > at : true))
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
}

export default function ExtraNote({ extra, venue }: { extra: Extra; venue?: Venue }) {
  const where = venue?.name ?? null;
  const when = extra.end
    ? `${fmtTime(extra.start)}–${fmtTime(extra.end)}`
    : `from ${fmtTime(extra.start)}`;

  // Assembled as one sentence rather than as columns: at this size a row of
  // labelled cells is more furniture than content, and the whole line is about
  // forty characters.
  const detail = [when, where].filter(Boolean).join(' · ');

  return (
    <Link className="extra-note" to={`/map/${extra.venueId}`}>
      <span className="extra-note-icon" aria-hidden="true"><IconLeaf size={15} /></span>
      <span className="extra-note-main">
        <span>
          <strong>{extra.name} today</strong>{' '}
          <span className="extra-note-detail">{detail}</span>
        </span>
        {/* Its own line rather than a third item on the first one. The when and
            the where change daily and are why a guest looks; the note is the
            same every day and is a condition on going. Run together they make
            one sixty-character line that wraps anyway, and the wrap falls
            wherever the venue name happens to end. */}
        {extra.note && <span className="extra-note-detail">{extra.note}</span>}
      </span>
      {/* Held out of the wrap: the text reflows under it on a narrow rail and
          the arrow stays pinned right, which is the lesson the two-up shelf
          above it already learned. */}
      <span className="extra-note-chev" aria-hidden="true">&rsaquo;</span>
    </Link>
  );
}
